import { THREE } from "@adsk/lmv";

const _boxArr = new Float32Array(6);
const _mat4 = new THREE.Matrix4();
const _vec3 = new THREE.Vector3();
const _vec4 = new THREE.Vector4();

export const UI_POS_TYPES = {
	World3D: 0,
	AnimatedWorld3D: 1,
	Client2D: 2,
};

class Canvas2DPosition {
	constructor(pos2D) {
		this.cached2D = pos2D ?? new THREE.Vector2(-Infinity, -Infinity);
		this.depth = -Infinity;
	}

	addCanvas2DListener(listener) {
		this.listener = listener;
	}

	setPos2D(x, y) {
		// fast-truncates screen position.
		this.cached2D.set(x << 0, y << 0);
		this.listener?.onCanvas2DChange(this.cached2D, false);
	}

	setDepth(depth) {
		this.depth = depth;
	}

	updateCanvasPosition() {}
}

// Static 3D position in world space.
export class WorldPos3D extends Canvas2DPosition {
	type = UI_POS_TYPES.World3D;

	constructor(pos3D) {
		super();
		this.origin = pos3D;
	}

	getPos3D() {
		return this.origin;
	}

	setPos3D(pos3D) {
		this.origin = pos3D;
	}

	/** [w2c] Optional world 2 client func. */
	onCameraUpdate(w2c) {
		if (this.origin) {
			w2c(this.origin, this);
		}
	}

	updateCanvasPosition(viewer) {
		if (this.origin) {
			setCanvas2DFrom3DPosition(this.origin, this, viewer.getCamera());
		}
	}
}

// 3D world position, tracking animated transitions based on the element's fragment transforms.
export class AnimatedWorldPos3D extends Canvas2DPosition {
	type = UI_POS_TYPES.AnimatedWorld3D;

	constructor(model, dbId, pos3D) {
		super();
		this.model = model;
		this.dbId = dbId;
		this.fids = []; // Precached fragments. (req. for animated position)
		model.getInstanceTree().enumNodeFragments(this.dbId, (fid) => { this.fids.push(fid); }, false);

		// Support element transition.
		this.animation = {
			lerp: 0.0,
			beg: new THREE.Vector3(),
			end: new THREE.Vector3(),
		};

		this.#initPosition(pos3D);
	}

	getPos3D() {
		// Element cannot be positioned yet
		if (!this.origin && !this.#initPosition()) return null;

		switch (this.animation.lerp) {
			case 0.0:
				return this.animation.beg;
			case 1.0:
				return this.animation.end;
			default:
				return _vec3.lerpVectors(this.animation.beg, this.animation.end, this.animation.lerp);
		}
	}

	animate(lerp) {
		this.animation.lerp = lerp;
	}

	/** [w2c] Optional world 2 client func. */
	onCameraUpdate(w2c) {
		const pos3D = this.getPos3D();
		if (pos3D) {
			w2c(pos3D, this);
		}
	}

	updateCanvasPosition(viewer) {
		const pos3D = this.getPos3D();
		if (pos3D) {
			setCanvas2DFrom3DPosition(pos3D, this, viewer.getCamera());
		}
	}

	// Sets the animated end position, use after change in anim matrix. (i.e 1 frame after the transition starts)
	preTransition(clusterSetLayout, sceneAnimState, useGPUAnimation) {
		// Attempting to animate element with no defined position. Let's wait.
		if (!this.origin && !this.#initPosition()) return;

		if (this.animation.lerp) {
			useGPUAnimation ? this.postTransition() : this.interruptTransition(); // Smooth transition between two animations
		}

		if (clusterSetLayout == null && sceneAnimState == null) {
			// Move back to the original position
			this.animation.end.copy(this.origin);
		} else if (clusterSetLayout) {
			// WebGPU codepath, move to animated position
			this.#setAnimatedPosition(this.animation.end);
		} else if (sceneAnimState) {
			// WebGL codepath, move to animated position
			this.#setAnimatedPositionFromState(this.animation.end, sceneAnimState);
		}
	}

	interruptTransition() {
		this.animation.beg.copy(this.getPos3D());
		this.animation.lerp = 0.0;
	}

	postTransition() {
		this.animation.lerp = 0.0;
		this.animation.beg.copy(this.animation.end);
	}

	#initPosition(pos3D) {
		// Base unanimated position, can be null if element is hidden or not loaded.
		this.origin = pos3D ?? getCenterPoint(this.model, this.dbId);
		// Initial animated placement if available.
		this.origin && this.#setAnimatedPosition(this.animation.beg);

		return this.origin;
	}

	#setAnimatedPosition(pos3D) {
		pos3D.copy(this.origin);

		for (const fragId of this.fids) {
			if (this.model.getAnimTransformMatrix(fragId, _mat4)) {
				pos3D.applyMatrix4(_mat4);
				return; // Any transformation will do.
			}
		}
	}

	#setAnimatedPositionFromState(pos3D, sceneAnimState) {
		pos3D.copy(this.origin);

		const modelAnimState = sceneAnimState.animStates[this.model.id];
		if (modelAnimState?.model !== this.model) return;

		const objAnim = modelAnimState.animStates[this.dbId];
		if (!objAnim) return;

		_mat4.compose(objAnim.move, objAnim.rot, objAnim.scale);
		pos3D.applyMatrix4(_mat4);
	}
}

// Static 2D position in client space.
export class ClientPos2D extends Canvas2DPosition {
	type = UI_POS_TYPES.Client2D;
}

export function getCenterPoint(model, dbId, dst) {
	model.getInstanceTree().getNodeBox(dbId, _boxArr);

	if (_boxArr[0] > _boxArr[3]) {
		// Hidden element
		return null;
	}

	dst ??= new THREE.Vector3();
	dst.x = 0.5 * (_boxArr[0] + _boxArr[3]);
	dst.y = 0.5 * (_boxArr[1] + _boxArr[4]);
	dst.z = 0.5 * (_boxArr[2] + _boxArr[5]);

	return dst;
}

export function getWorldBox(model, dbId, dst) {
	model.getInstanceTree().getNodeBox(dbId, _boxArr);

	if (_boxArr[0] > _boxArr[3]) {
		// Hidden element
		return null;
	}

	dst ??= new THREE.Box3();
	dst.min.set(_boxArr[0], _boxArr[1], _boxArr[2]);
	dst.max.set(_boxArr[3], _boxArr[4], _boxArr[5]);

	return dst;
}

export function setCanvas2DFrom3DPosition(pos3D, dst, camera, optViewProjMatrix) {
	_vec4.set(pos3D.x, pos3D.y, pos3D.z, 1);

	if (optViewProjMatrix) {
		_vec4.applyMatrix4(optViewProjMatrix);
	} else {
		_vec4.applyMatrix4(camera.matrixWorldInverse);
		_vec4.applyMatrix4(camera.projectionMatrix);
	}

	// Perspective divide for points in front of the camera.
	if (_vec4.w > 0) _vec4.divideScalar(_vec4.w);

	const clientX = (_vec4.x + 1) * 0.5 * camera.clientWidth - 0.5;
	const clientY = (_vec4.y - 1) * -0.5 * camera.clientHeight - 0.5;
	const clientZ = (_vec4.z + 1) * 0.5;

	dst.setPos2D(clientX, clientY);
	dst.setDepth(clientZ);

	return dst;
}
